import { Icon } from '@britishcouncil/react-admin';
import styled from '@emotion/styled';
import { AllocationGrid } from './AllocationGrid';
import { SpeakingSessionRoomStructureDto } from 'api/planning';
import { SpeakingSlotAction } from './SpeakingSlotAction';
import { useRoomStructure } from './useRoomStructure';
import classNames from 'classnames';
import { RoomClashPeriodDto } from './utils/calculateClashedPeriods';

type Props = {
  speakingSessionDate?: string;
  sessionRoomId?: number;
  localSpeakingSlotMinutesOffset: number;
  structure: SpeakingSessionRoomStructureDto;
  structures: SpeakingSessionRoomStructureDto[];
  clashedPeriods: RoomClashPeriodDto[];
  selectedSlotAction: SpeakingSlotAction;
  editing: boolean;
  onDelete: (id: number) => void;
  onChangeStructure: (value: SpeakingSessionRoomStructureDto) => void;
  roomCapacity: number;
};

export const RoomStructure = ({
  speakingSessionDate,
  sessionRoomId,
  localSpeakingSlotMinutesOffset,
  structure,
  structures,
  clashedPeriods,
  editing,
  selectedSlotAction,
  onDelete,
  onChangeStructure,
  roomCapacity,
}: Props) => {
  const {
    ref,
    handleDeleteClick,
    occupiedSlots,
    roomSwaSlots,
    speakingSlotType,
    onChangeSlots,
    onChangeCapacity,
    minCapacity,
    hasAnyBookedSlot,
  } = useRoomStructure({ structure, structures, editing, onDelete, onChangeStructure });

  const plannedCapacity = structure.speakingSlots[0]?.plannedCapacity ?? roomCapacity;

  return (
    <S.Subgrid>
      <S.RowLeft className={`left-panel ${speakingSlotType.color}`}>
        {speakingSlotType.label}
      </S.RowLeft>
      <S.RowCenter ref={ref}>
        <AllocationGrid
          sessionRoomId={sessionRoomId}
          sessionRoomStructureId={structure.id}
          localSpeakingSlotMinutesOffset={localSpeakingSlotMinutesOffset}
          slots={structure.speakingSlots}
          occupiedSlots={occupiedSlots}
          roomSwaSlots={roomSwaSlots}
          clashedPeriods={clashedPeriods}
          editing={editing}
          selectedSlotAction={selectedSlotAction}
          speakingSlotType={structure.speakingSlotType}
          assignedExaminer={structure.assignedExaminer}
          speakingSessionDate={speakingSessionDate}
          onChangeSlots={onChangeSlots}
          roomCapacity={roomCapacity}
          plannedCapacity={plannedCapacity}
          minCapacity={minCapacity}
          onCapacityChange={onChangeCapacity}
        />
      </S.RowCenter>
      <S.RowRight
        className={classNames('right-panel', { disabled: hasAnyBookedSlot })}
        onClick={handleDeleteClick}
        data-testid="delete-room-structure"
      >
        {editing ? <Icon color="red" name="trash" /> : null}
      </S.RowRight>
    </S.Subgrid>
  );
};

const S = {
  Subgrid: styled.div`
    position: relative;
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
  `,
  RowLeft: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    border-bottom: 1px solid #fff;
    border-left: 1px solid #e2e2e3;
    color: #fff;
    font-size: 12px;
    font-weight: bold;
    &.grey {
      background-color: #607582;
    }
    &.pink {
      background-color: #de3494;
    }
    &.teal {
      background-color: #00b5ad;
    }
    &.violet {
      background-color: #a333c8;
    }
  `,
  RowCenter: styled.div`
    overflow-x: auto;
    scrollbar-width: none;

    &:after {
      content: '';
      position: absolute;
      box-shadow:
        inset -10px 0 10px -10px #d5d5d5,
        inset 10px 0 10px -10px #d5d5d5;
      width: 100%;
      height: 100%;
      grid-column: 2 / 3;
      top: 0;
      pointer-events: none;
    }
  `,
  RowRight: styled.div`
    background-color: #fafafb;
    border-bottom: 1px solid #e2e2e3;
    border-right: 1px solid #e2e2e3;
    display: flex;
    justify-content: center;
    align-items: center;

    & > i.icon {
      margin: 0;
      font-size: 18px;
    }
    .editing > div > div > & {
      cursor: pointer;
      &:hover,
      &:focus {
        background-color: #ededed;
      }
      &:active {
        background-color: #e2e2e3;
      }
      &.disabled {
        cursor: default;
        pointer-events: none;

        & > i.icon {
          opacity: 0.45;
        }
      }
    }
  `,
};
